import React, { useMemo } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSystemKills, formatIsk, KillboardEntityActivity, KillboardResponse, ZkbKill } from '../api/killboard';
import { fetchSystemActivity } from '../api/systems';
import { useSystemBase } from '../pages/system/SystemPage';
import ConsolePanel from './ConsolePanel';
import EffectStats from './EffectStats';
import LcarsRule from './LcarsRule';
import PanelHeader from './PanelHeader';
import PlanetIcon from './PlanetIcon';
import KspacePanel from './KspacePanel';
import StatusBadge from './StatusBadge';
import { C, FONT, glow, classColor, frameSx, hexToRgba, lcarsLabelSx, prefersReducedMotion } from '../theme/tokens';
import { SdeMoon, SdePlanet, SystemActivityEntry, SystemIntel } from '../types';

/** Fixed at 30 for this panel: it shows 24h/7d/30d side by side rather than
 * letting a toggle pick one, so the fetch always needs the deepest window —
 * the full detailed window-picker lives on the system's own Killboard tab. */
const KILLBOARD_WINDOW_DAYS = 30;

/** The body of a system's intel — statics/effect/planets+moons/NPC sites/
 * killboard (with activity analytics)/corp activity. Pure intel, no manual
 * data-entry workflow here (signature scanning was removed — that's an
 * operational task, not intel; corp-logged wormhole connections still live
 * in the Data Vault, a different feature).
 *
 * The system's name/class/region header is NOT here: it belongs to
 * SystemPage, which shows it above the tab strip so it stays put across the
 * Overview/Sites/Killboard tabs instead of being re-rendered per tab.
 *
 * `intel` is fetched by the caller so this component stays purely
 * presentational. `showCorpActivity` is the one auth-aware bit: everything
 * else here is public EVE reference data plus a public killboard, but the
 * corp activity trail is corp-logged and must stay off the public
 * /system/:slug page for anonymous visitors. */
const SystemIntelPanel: React.FC<{ intel: SystemIntel; showCorpActivity?: boolean }> = ({
  intel,
  showCorpActivity = true,
}) => {
  const navigate = useNavigate();
  const base = useSystemBase();
  const { system, statics, staticsCatalogued, wandering, effect, siteCatalog, gasSites, oreSites, iceSites, resources, planets, moons } = intel;
  // A system is known space exactly when it carries no wormhole class.
  //
  // This used to test for system_class === 'highsec' | 'lowsec' | 'nullsec'.
  // Those strings appear in 001_init.sql's column comment but the SDE import
  // never writes them: verified against the live DB, 0 rows hold any of the
  // three, every J-space row (31xxxxxx) HAS a class, and no non-J-space row
  // does. So the old test was false for every real K-space system and the
  // K-space dashboard below never rendered once — which is why known space
  // looked identical to a wormhole, skyhooks included.
  const isKSpace = !system.system_class;
  // Site links stay on the page they were opened from (/system/<name> or the
  // live /intel/current), so the live page keeps following the character.
  // `?system=` is what makes the site page layer THIS system's effect over the
  // base site data (see routes/sites.ts) — the path is for humans and
  // back-navigation, the query param is the data context.
  const openSite = (className: string, siteName: string) =>
    navigate(
      `${base}/sites/${encodeURIComponent(className)}/${encodeURIComponent(siteName)}?system=${system.solar_system_id}`,
    );

  const kills = useQuery({
    queryKey: ['killboard', system.solar_system_id, KILLBOARD_WINDOW_DAYS],
    queryFn: () => fetchSystemKills(system.solar_system_id, KILLBOARD_WINDOW_DAYS),
  });
  const activity = useQuery({
    queryKey: ['system-activity', system.solar_system_id],
    queryFn: () => fetchSystemActivity(system.solar_system_id),
    enabled: showCorpActivity,
  });

  const moonsByPlanet = useMemo(() => {
    const map = new Map<number, SdeMoon[]>();
    for (const m of moons) {
      if (!map.has(m.orbit_planet_id)) map.set(m.orbit_planet_id, []);
      map.get(m.orbit_planet_id)!.push(m);
    }
    return map;
  }, [moons]);

  // The J/K split. A known-space system's questions (hubs, sovereignty,
  // who's shooting, nearby skyhooks) have almost nothing in common with a
  // wormhole's (statics, effect, Sleeper sites), so K-space gets its own
  // dashboard rather than this one with half its panels reading "n/a".
  if (isKSpace) {
    return <KspacePanel solarSystemId={system.solar_system_id} systemName={system.name} />;
  }

  return (
    <Stack spacing={3}>
      <SummaryStrip intel={intel} kills={kills.data} activity={activity.data} showCorpActivity={showCorpActivity} />

      {/* One grid for Effect + Statics + Planets rather than a section per
          pair. The two short panels stack in the left column beside the tall
          planet list, which is what stops a system with 11 planets leaving
          half a screen of dead space next to a three-line statics box. */}
      <SectionGroup label="System" accent={C.cyan}>
        <ConsolePanel title="Effect &amp; Bonuses" accent={C.gold} corners={false} sx={frameSx(C.gold)}>
          {!effect ? (
            <Typography sx={{ color: C.textDim }}>
              {isKSpace
                ? 'K-space — no wormhole secondary-sun effect applies here. Metaliminal storm status is not tracked by this tool yet: none reported.'
                : 'No system effect catalogued for this system yet.'}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: FONT.display,
                  color: C.text,
                  fontWeight: 700,
                  fontSize: '1.15rem',
                  letterSpacing: '0.02em',
                  textShadow: glow(C.gold, 14, 0.3),
                  mb: 0.5,
                }}
              >
                {effect.label}
              </Typography>
              <Typography sx={{ color: C.text, fontSize: '0.92rem', lineHeight: 1.55, mb: 1.5 }}>{effect.summary}</Typography>
              <EffectStats stats={effect.stats} />
            </>
          )}
        </ConsolePanel>

        {/* The old "Sleeper Threat" panel is gone: it restated one vague
            per-class sentence that the real per-site data (NPC waves,
            triggers, EWAR — see the NPC Sites panel and each site's own
            page) now answers properly. A generic line next to real wave
            compositions was the thing that read as wrong. */}

        {/* Everything that can connect here, in descending certainty:
            statics are always open, wandering holes come and go, and a K162
            is whatever somebody else opened from the far side. A scout
            reading only "2 statics" plans for two exits and gets surprised
            by the third. */}
        {/* Deliberately NOT a ConsolePanel: this is the one place on the page
            where the data IS a set of routes, so it is drawn as routes rather
            than listed inside a container. The page already owns a box-free
            section idiom (PanelHeader + LcarsRule, as SectionGroup uses for
            "System" and "Activity"), so dropping the frame here reads as page
            vocabulary rather than one panel losing its chrome. */}
        <Box sx={{ gridColumn: { md: '1' } }}>
          <PanelHeader label="Possible Connections" accent={C.cyan} dense sx={{ mb: 0.75 }} />
          <LcarsRule accent={C.cyan} sx={{ mb: 1.5 }} />

          <Typography sx={{ ...lcarsLabelSx(C.cyan, '0.72rem'), mb: 1 }}>Statics &mdash; always present</Typography>
          {!staticsCatalogued ? (
            <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>Not yet catalogued by anoik.is for this system.</Typography>
          ) : (
            <Stack spacing={0.25}>
              {statics.map((s, i) => (
                <ConnectionLine
                  key={s.static_type_code}
                  code={s.static_type_code}
                  leadsTo={s.leads_to_class}
                  index={i}
                  variant="static"
                />
              ))}
            </Stack>
          )}

          {wandering.length > 0 && (
            <>
              <Typography sx={{ ...lcarsLabelSx(C.gold, '0.72rem'), mt: 2, mb: 0.4 }}>Wandering &mdash; may appear</Typography>
              <Typography sx={{ color: C.textDim, fontSize: '0.74rem', mb: 1 }}>
                Any {system.system_class} can roll these. They are not guaranteed and they expire.
              </Typography>
              <Stack spacing={0.25}>
                {wandering.map((w, i) => (
                  <ConnectionLine
                    key={w.static_type_code}
                    code={w.static_type_code}
                    leadsTo={w.leads_to_class}
                    index={statics.length + i}
                    variant="wandering"
                  />
                ))}
              </Stack>
            </>
          )}

          {siteCatalog && (
            <>
              <Typography sx={{ ...lcarsLabelSx(C.neutral, '0.72rem'), mt: 2, mb: 1 }}>K162 &mdash; inbound</Typography>
              {/* Drawn pointing the other way: a K162 is the one connection
                  that arrives rather than departs, and the reversed conduit
                  says that before the sentence below does. */}
              <ConnectionLine
                code="K162"
                leadsTo="anywhere"
                index={statics.length + wandering.length}
                variant="inbound"
              />
              <Typography sx={{ color: C.textDim, fontSize: '0.74rem', mt: 1 }}>
                The far side of a hole someone opened into this system. Always possible, leads anywhere, and tells you
                nothing about where until you jump it or scan the other side.
              </Typography>
            </>
          )}

          {resources.length > 0 && (
            <>
              <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.76rem'), mt: 2, mb: 0.6 }}>
                Belts
              </Typography>
              <Typography sx={{ color: C.text }}>{resources.map((r) => r.variant).join(', ')}</Typography>
            </>
          )}
          {/* Contextual trust note, right where it's relevant — not a
              page-wide footnote a reader has to connect back to this panel
              themselves. Also corrected: statics are anoik.is-sourced by
              default now, corp-observed promotion is the exception, not
              the rule (see docs/DATA_CURATION.md). */}
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem', mt: 2 }}>
            Sourced from anoik.is; some entries may be admin-promoted from corp observation.
          </Typography>
        </Box>

        {/* Explicitly parked in column 2 across both rows — Killboard is
            the tallest thing here (histogram + active-corps + recent kills)
            and the two short panels stack beside it instead of leaving a
            void, same reasoning the old Planets slot used, just with
            Killboard now in the higher-visibility spot: "who's dying here"
            outranks the celestial list for a scout deciding whether to
            stay. Planets moved down into the Activity row below. */}
        <ConsolePanel
          title="Killboard"
          accent={C.cyan}
          corners={false}
          scrollBody
          sx={{ ...frameSx(C.cyan), gridColumn: { md: '2' }, gridRow: { md: '1 / span 2' } }}
          action={
            <Typography
              onClick={() => navigate(`${base}/killboard`)}
              sx={{ ...lcarsLabelSx(C.blue, '0.68rem'), cursor: 'pointer', '&:hover': { color: C.cyan } }}
            >
              Full killboard &rarr;
            </Typography>
          }
        >
          {kills.isLoading ? (
            <CircularProgress size={20} sx={{ color: C.cyan }} />
          ) : kills.isError ? (
            <Typography sx={{ color: C.textDim }}>Killboard unavailable right now.</Typography>
          ) : !kills.data || kills.data.stats.totalKills === 0 ? (
            <Typography sx={{ color: C.textDim }}>No kills in the last 30 days.</Typography>
          ) : (
            <Stack spacing={1.75}>
              <RollingWindowSummary rollingWindows={kills.data.stats.rollingWindows} mostRecentKill={kills.data.stats.mostRecentKill} />
              <KillboardSparkline days={kills.data.stats.dailyActivity} />

              <LcarsRule accent={C.cyan} />

              <EntitySection
                label="Alliances"
                accent={C.blue}
                entities={kills.data.activeAlliances}
                kills={kills.data.kills}
                field="allianceId"
              />
              {kills.data.activeAlliances.length > 0 && kills.data.activeCorps.length > 0 && <LcarsRule accent={C.cyan} />}
              <EntitySection
                label="Corporations"
                accent={C.cyan}
                entities={kills.data.activeCorps}
                kills={kills.data.kills}
                field="corporationId"
              />
            </Stack>
          )}
        </ConsolePanel>

      </SectionGroup>

      {/* Activity now owns the three things that describe what there is to
          DO here, in the order a scout asks for them: what content is in the
          system, what the celestials offer, and what the corp has already
          done. Each is a titled category inside one group with its own
          divider, rather than three unrelated panels floating in a grid. */}
      <SectionGroup label="Activity" accent={C.cyan} single>
        <ConsolePanel title="Content Sites" accent={C.cyan} corners={false} sx={frameSx(C.cyan)}>
          {!siteCatalog ? (
            <Typography sx={{ color: C.textDim }}>
              {isKSpace
                ? 'K-space content is anomalies and belts rather than a fixed W-space site catalogue.'
                : 'No site catalogue for this space type.'}
            </Typography>
          ) : (
            <Stack divider={<CategoryDivider />} spacing={1.5}>
              <SiteRow
                label="Combat"
                names={siteCatalog.combatSites}
                accent={C.red}
                onOpen={(name) => openSite(system.system_class ?? 'unknown', name)}
              />
              <SiteRow
                label="Relic / Data"
                names={siteCatalog.relicDataSites}
                accent={C.gold}
                onOpen={(name) => openSite(system.system_class ?? 'unknown', name)}
              />
              {/* Gas/ore sites are the same universal rarity-tier pool
                  regardless of class — see backend whSiteCatalog.ts — so
                  they route to a class-independent "gas"/"ore" bucket. */}
              <SiteRow label="Gas" names={gasSites} accent={C.green} onOpen={(name) => openSite('gas', name)} />
              <SiteRow label="Ore" names={oreSites} accent={C.wormhole} onOpen={(name) => openSite('ore', name)} />
              {iceSites.length > 0 && (
                <SiteRow label="Ice" names={iceSites} accent={C.cyan} onOpen={(name) => openSite('ore', name)} />
              )}
            </Stack>
          )}
        </ConsolePanel>

        <ConsolePanel title="Planets" accent={C.wormhole} corners={false} sx={frameSx(C.wormhole)}>
          {planets.length === 0 ? (
            <Typography sx={{ color: C.textDim }}>No planet data imported for this system yet.</Typography>
          ) : (
            <Stack divider={<CategoryDivider />} spacing={1}>
              {planets.map((p: SdePlanet) => {
                const planetMoons = moonsByPlanet.get(p.planet_id) ?? [];
                return (
                  <Box key={p.planet_id}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <PlanetIcon planetType={p.planet_type} />
                      <Typography sx={{ color: C.text, fontFamily: FONT.mono, fontSize: '0.85rem' }}>{p.name}</Typography>
                      {/* Planet type and moon count are words, so they take
                          the label voice. The planet's NAME above stays mono —
                          that one is an identifier. */}
                      {p.planet_type && (
                        <Typography sx={{ color: C.textDim, fontFamily: FONT.sub, fontWeight: 500, fontSize: '0.82rem' }}>
                          {p.planet_type.replace(/^Planet \((.*)\)$/, '$1')}
                        </Typography>
                      )}
                      {planetMoons.length > 0 && (
                        <Typography sx={{ color: C.textDim, fontFamily: FONT.sub, fontWeight: 500, fontSize: '0.82rem' }}>
                          &middot; {planetMoons.length} moon{planetMoons.length === 1 ? '' : 's'}
                        </Typography>
                      )}
                    </Stack>
                    {p.piResources && (
                      <Typography sx={{ color: hexToRgba(C.cyan, 0.95), fontFamily: FONT.sub, fontWeight: 500, fontSize: '0.84rem' }}>
                        PI: {p.piResources.join(', ')}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </ConsolePanel>

      </SectionGroup>
    </Stack>
  );
};

/** One named cluster of panels — a `PanelHeader` (dense, no frame of its
 * own) over a responsive panel grid, so "Danger"/"Opportunity"/"Activity"
 * read as three distinct zones of the same instrument instead of six
 * equal-weight panels with no hierarchy (the old flat layout this
 * replaces). Matches Dashboard's `SystemPillRow` idiom — group structure
 * comes from a header + spacing, never a wrapping filled box. */
const SectionGroup: React.FC<{
  label: string;
  accent: string;
  children: React.ReactNode;
  single?: boolean;
  /** Optional control pinned to the group header's right edge. */
  action?: React.ReactNode;
}> = ({
  label,
  accent,
  children,
  single,
  action,
}) => (
  <Box>
    {/* Header + asymmetric LCARS rule: the zone label reads as a console
        section marker rather than a bare heading, and the rule's uneven
        segments do the separating a hairline border used to. */}
    <PanelHeader label={label} accent={accent} dense action={action} sx={{ mb: 0.75 }} />
    <LcarsRule accent={accent} sx={{ mb: 1.5 }} />
    {/* alignItems:'start' lets a short panel keep its own height instead of
        being stretched to match a tall neighbour. The "System" group's
        Killboard panel used to force-stretch to match the left column's
        combined height, which left it as a large empty bordered box on any
        system with a short kill history — worse than the column simply
        ending early. */}
    <Box
      sx={{
        display: 'grid',
        // `single` stacks the group in one column — used by Activity, whose
        // three categories are meant to read as a sequence, not a mosaic.
        gridTemplateColumns: single ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      {children}
    </Box>
  </Box>
);

/** The divider between categories inside a panel — a hairline that fades out
 * from the left, matching `lcarsRuleSx`'s language at a quieter weight. This
 * is what makes a grouped panel read as separated categories rather than one
 * undifferentiated run of rows. */
const CategoryDivider: React.FC = () => (
  <Box
    sx={{
      height: '1px',
      border: 'none',
      background: `linear-gradient(90deg, ${hexToRgba(C.wormhole, 0.35)} 0%, ${hexToRgba(C.wormhole, 0.08)} 55%, transparent 100%)`,
    }}
  />
);

/** One category of sites (Combat/Relic/Data/Gas/Ore) as clickable
 * StatusBadges instead of a flat comma-joined string — each opens that
 * site's detail page (spawn composition, blueloot, salvage, hacking
 * difficulty — see pages/sites). */
const SiteRow: React.FC<{ label: string; names: string[]; accent: string; onOpen: (name: string) => void }> = ({
  label,
  names,
  accent,
  onOpen,
}) => (
  <Box>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.74rem'), mb: 0.6 }}>{label}</Typography>
    {names.length === 0 ? (
      <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>&mdash;</Typography>
    ) : (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {names.map((name) => (
          <Box
            key={name}
            onClick={() => onOpen(name)}
            sx={{ cursor: 'pointer', transition: 'transform 0.15s ease', '&:hover': { transform: 'translateY(-1px)' } }}
          >
            <StatusBadge label={name} accent={accent} />
          </Box>
        ))}
      </Stack>
    )}
  </Box>
);

/** At-a-glance strip above the grouped panels — statics/effect/kill/corp
 * activity signal in one scan, so a viewer doesn't have to read six panels
 * to know whether this system is worth stopping in. Pure presentation over
 * data the panels below already fetch (no extra network calls). */
const SummaryStrip: React.FC<{
  intel: SystemIntel;
  kills: KillboardResponse | undefined;
  activity: SystemActivityEntry[] | undefined;
  showCorpActivity: boolean;
}> = ({ intel, kills, activity, showCorpActivity }) => {
  const { statics, staticsCatalogued, effect, siteCatalog } = intel;
  const staticsLabel = staticsCatalogued ? `${statics.length} static${statics.length === 1 ? '' : 's'}` : 'statics uncatalogued';
  const hasKills = !!kills && kills.stats.totalKills > 0;
  const hasActivity = !!activity && activity.length > 0;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <StatusBadge label={staticsLabel} accent={staticsCatalogued ? C.cyan : C.neutral} dim={!staticsCatalogued} />
      <StatusBadge label={effect ? effect.label : 'no effect'} accent={effect ? C.gold : C.neutral} dim={!effect} />
      <StatusBadge
        label={hasKills ? `${kills!.stats.rollingWindows.days30} kills / 30d` : 'no kills / 30d'}
        accent={hasKills ? C.red : C.neutral}
        dim={!hasKills}
      />
      {showCorpActivity && (
        <StatusBadge
          label={hasActivity ? 'corp activity logged' : 'no corp activity'}
          accent={hasActivity ? C.blue : C.neutral}
          dim={!hasActivity}
        />
      )}
    </Stack>
  );
};

/** "24h · 7d · 30d" as text, in place of the old raw hourly-by-UTC bar chart
 * (user report: "the UTC time is not relevant") — three rolling counts
 * measured back from now, which answers "is this hot right now" at a glance
 * without asking the reader to first convert to their own timezone. */
const RollingWindowSummary: React.FC<{
  rollingWindows: { hours24: number; days7: number; days30: number };
  mostRecentKill: { timestamp: string; daysAgo: number } | null;
}> = ({ rollingWindows: rw, mostRecentKill }) => (
  <Box>
    <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
      <WindowCount label="Last 24 hours" count={rw.hours24} />
      <WindowCount label="Last 7 days" count={rw.days7} />
      <WindowCount label="Last 30 days" count={rw.days30} />
    </Stack>
    {mostRecentKill && (
      <Typography sx={{ color: C.textDim, fontSize: '0.76rem', mt: 0.75 }}>
        Most recent kill {mostRecentKill.daysAgo < 1 ? `${Math.round(mostRecentKill.daysAgo * 24)}h` : `${mostRecentKill.daysAgo.toFixed(1)}d`}{' '}
        ago
      </Typography>
    )}
  </Box>
);

const WindowCount: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <Box>
    <Typography sx={{ color: count > 0 ? C.text : C.textDim, fontFamily: FONT.mono, fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.1 }}>
      {count}
    </Typography>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem') }}>{label}</Typography>
  </Box>
);

/** The "small activity timer" — a compact, non-interactive 30-day sparkline
 * (reusing the same daily buckets the full Killboard tab charts in detail).
 * No hover readout, no axis labels: this is a shape at a glance, not the
 * detailed per-day/per-hour instrument that page already owns. */
const KillboardSparkline: React.FC<{ days: { date: string; total: number }[] }> = ({ days }) => {
  if (!Array.isArray(days) || days.length === 0) return null;
  const max = Math.max(1, ...days.map((d) => d.total));
  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem'), mb: 0.5 }}>Activity — last {days.length} days</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 22 }}>
        {days.map((d) => (
          <Box
            key={d.date}
            sx={{
              flex: 1,
              minWidth: 2,
              height: d.total === 0 ? '2px' : `${Math.max(10, (d.total / max) * 100)}%`,
              borderRadius: '1px',
              bgcolor: d.total === 0 ? hexToRgba(C.text, 0.14) : hexToRgba(C.cyan, 0.35 + 0.55 * (d.total / max)),
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

/** Kill count entries this app enriches into a full display kill (see
 * `buildDisplayKills` server-side — capped at 20, not every kill in the
 * window), keyed by the corp/alliance ID field that links a kill to an
 * entity below. */
type EntityKillField = 'corporationId' | 'allianceId';

/** One "who's active here" section (Alliances or Corporations): each entity
 * ranked by kill-count appearances, with up to two sample kills right under
 * its name where the enriched sample happens to include one — who died in
 * what, for how much, killed solo or by a gang of N, linking straight to
 * zKillboard. Not every top entity will have a sample (the enrichment only
 * covers the most recent 20 kills of up to hundreds in a busy hole); that's
 * shown honestly rather than papered over. */
const EntitySection: React.FC<{
  label: string;
  accent: string;
  entities: KillboardEntityActivity[];
  kills: ZkbKill[];
  field: EntityKillField;
}> = ({ label, accent, entities, kills, field }) => {
  if (entities.length === 0) return null;
  // Top 5, not all 8 the backend ranks: this panel is a compact companion to
  // the full Killboard tab, not a replacement for it.
  const top = entities.slice(0, 5);
  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.72rem'), mb: 0.75 }}>{label}</Typography>
      <Stack spacing={1.25}>
        {top.map((entity) => {
          const sample = kills.filter((k) => k.victim[field] === entity.id || k.finalBlow?.[field] === entity.id).slice(0, 2);
          return (
            <Box key={entity.id}>
              <StatusBadge label={`${entity.name} (${entity.kills})`} accent={accent} />
              {sample.length > 0 ? (
                <Stack spacing={0.5} sx={{ mt: 0.5, pl: 1.5, borderLeft: `1px solid ${C.hairline}` }}>
                  {sample.map((k) => (
                    <KillSampleRow key={k.killmail_id} kill={k} />
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: C.textDim, fontSize: '0.7rem', mt: 0.4, pl: 1.5 }}>
                  no kill in the recent sample — see full killboard
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/** Who died flying what, for how much, and who landed the shot — solo or
 * backed by a gang of N — linking straight out to the killmail. Same facts
 * the system's full Killboard tab shows per kill, condensed to one line. */
const KillSampleRow: React.FC<{ kill: ZkbKill }> = ({ kill: k }) => (
  <Box
    component="a"
    href={`https://zkillboard.com/kill/${k.killmail_id}/`}
    target="_blank"
    rel="noreferrer"
    sx={{ display: 'block', textDecoration: 'none' }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} flexWrap="wrap">
      <Typography sx={{ fontSize: '0.76rem' }}>
        <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>
          {k.victim.name}
        </Box>{' '}
        <Box component="span" sx={{ color: C.textDim }}>
          ({k.victim.shipTypeName})
        </Box>
      </Typography>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.text, flexShrink: 0 }}>{formatIsk(k.totalValue)}</Typography>
    </Stack>
    {k.finalBlow && (
      <Typography sx={{ color: C.textDim, fontSize: '0.68rem' }}>
        shot by <Box component="span" sx={{ color: C.red, fontWeight: 600 }}>{k.finalBlow.name}</Box>{' '}
        {k.attackerCount > 1 ? `+ ${k.attackerCount - 1} other${k.attackerCount - 1 === 1 ? '' : 's'}` : '(solo)'}
      </Typography>
    )}
  </Box>
);

/** Stagger ceiling for the conduit draw-in. Past this the list reads as
 * loading rather than opening, however many holes a system has. */
const CONDUIT_STAGGER_MS = 70;
const CONDUIT_MAX_DELAY_MS = 280;

/**
 * One connection, drawn instead of boxed.
 *
 * The conduit between the hole's code and its destination is the data, not
 * decoration: a solid line is a static that is always open, a dashed one is
 * a wandering hole that may not be there at all, and the colour is the
 * danger class it comes out in — so "where does this lead" reads along the
 * line before either label is parsed. `inbound` reverses the whole thing,
 * because a K162 is the one connection that arrives rather than departs.
 *
 * Motion is budgeted to two moments: the conduits draw outward from their
 * codes once on mount (staggered, capped), then hold still. Hovering or
 * focusing a row sends a single spark down that conduit to its destination.
 * Nothing loops — five holes each pulsing forever is ambient noise, and it
 * is what the old row's infinite `edgeFlow` chevron was doing.
 */
const ConnectionLine: React.FC<{
  code: string;
  leadsTo: string;
  index: number;
  variant: 'static' | 'wandering' | 'inbound';
}> = ({ code, leadsTo, index, variant }) => {
  const reduced = prefersReducedMotion();
  const inbound = variant === 'inbound';
  const wandering = variant === 'wandering';
  // An inbound K162 leads nowhere knowable, so it gets the neutral tone
  // rather than a class colour it has not earned.
  const accent = inbound ? C.neutral : classColor(leadsTo);
  const delay = Math.min(index * CONDUIT_STAGGER_MS, CONDUIT_MAX_DELAY_MS);
  const muted = wandering || inbound;

  /* An authored SVG marker rather than a glyph or a zero-size CSS triangle:
     it stays crisp at any zoom, and it is one drawn shape instead of three
     borders pretending to be one. Points whichever way the hole runs. */
  const arrow = (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 6 8"
      sx={{ width: 6, height: 8, flexShrink: 0, display: 'block', opacity: muted ? 0.75 : 1 }}
    >
      <polygon points={inbound ? '6,0 6,8 0,4' : '0,0 0,8 6,4'} fill={accent} />
    </Box>
  );

  const label = (text: string) => (
    <Typography
      sx={{ fontFamily: FONT.mono, fontSize: '0.92rem', color: muted ? C.textDim : C.text, flexShrink: 0 }}
    >
      {text}
    </Typography>
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      tabIndex={0}
      sx={{
        py: 0.85,
        outline: 'none',
        cursor: 'default',
        // Armed from the row, so the whole line is the target rather than the
        // 1px conduit itself.
        '&:hover .conduit-spark, &:focus-visible .conduit-spark': {
          animation: reduced ? 'none' : 'conduitSpark 0.6s cubic-bezier(0.16,1,0.3,1)',
          animationDirection: inbound ? 'reverse' : 'normal',
        },
        '&:hover .conduit-line, &:focus-visible .conduit-line': { borderTopColor: accent },
        '&:hover .conduit-dest, &:focus-visible .conduit-dest': { color: accent },
        // Translating a full-width carrier is how the spark crosses the
        // conduit without animating `left` — that would be a layout property
        // on a hairline that sits in a flex row.
        '@keyframes conduitSpark': {
          from: { transform: 'translateX(0)', opacity: 0 },
          '18%': { opacity: 1 },
          '82%': { opacity: 1 },
          to: { transform: 'translateX(100%)', opacity: 0 },
        },
      }}
    >
      <Box sx={{ minWidth: 46, flexShrink: 0 }}>{label(code)}</Box>
      {inbound && arrow}

      {/* The conduit itself. `height: 0` + a top border keeps it a true
          hairline at any zoom, and scaleX from the origin end is composited,
          so the line extends rather than fading in. */}
      <Box
        className="conduit-line"
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 24,
          height: 0,
          borderTop: `1px ${muted ? 'dashed' : 'solid'} ${hexToRgba(accent, muted ? 0.4 : 0.65)}`,
          transition: 'border-top-color 0.2s ease',
          transformOrigin: inbound ? 'right center' : 'left center',
          animation: reduced ? 'none' : `conduitDraw 0.55s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
          '@keyframes conduitDraw': {
            from: { transform: 'scaleX(0)', opacity: 0 },
            to: { transform: 'scaleX(1)', opacity: 1 },
          },
        }}
      >
        <Box
          className="conduit-spark"
          aria-hidden
          sx={{ position: 'absolute', top: -2, left: 0, width: '100%', height: 4, opacity: 0, pointerEvents: 'none' }}
        >
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: accent,
              boxShadow: `0 0 8px ${hexToRgba(accent, 0.9)}`,
            }}
          />
        </Box>
      </Box>

      {!inbound && arrow}
      {/* Fixed-width and left-aligned so every arrowhead lands on the same
          vertical axis: destinations run from "C5" to "highsec", and letting
          the column size to its content gave the fan a ragged terminal edge
          with each conduit stopping somewhere different. */}
      <Typography
        className="conduit-dest"
        sx={{
          fontFamily: FONT.mono,
          fontSize: '0.92rem',
          color: muted ? C.textDim : C.text,
          transition: 'color 0.2s ease',
          flexShrink: 0,
          minWidth: 68,
        }}
      >
        {leadsTo}
      </Typography>
    </Stack>
  );
};

export default SystemIntelPanel;
